import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { Message, MessageService } from 'primeng/api';
import { ScrollPanel } from 'primeng/scrollpanel';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
import { LazyLoadedWidgets } from 'src/app/app-routing-lazy';
import { RightPanelService } from 'src/app/components/rightpanel/rightpanel.service';
import { Role } from 'src/app/models/role';
import { Tenant } from 'src/app/models/tenant';
import { LayoutService } from 'src/app/services/layout.service';
import { MenuService } from 'src/app/services/menu/menu.service';
import { CommandLazyDataService } from 'src/app/services/task/command/command-lazy-data.service';
import { TenantService } from 'src/app/services/tenant/tenant.service';

import { AlertCommand, AlertMessage, AlertService, MessageSeverity } from '../../../services/alert.service';
import { AppTitleService } from '../../../services/app-title.service';
import { UserService } from '../../../services/user/user.service';
import { LazyLoaderBaseComponent } from '../../lazy-loader/lazy-loader.component';
import { LazyLoaderService } from '../../lazy-loader/lazy-loader.service';
import { MenuOrientation } from '../../menu/menu-models';
import { MainConfigService } from './main-config.service';
import { MediaService } from './media.service';


@Component({
    selector: 'app-main',
    templateUrl: './main.component.html',
    styleUrls: ['./main.component.scss']
})
export class MainLayoutComponent extends LazyLoaderBaseComponent implements AfterViewInit, OnDestroy, OnInit {

    layoutCompact = true;

    layoutMode: MenuOrientation = MenuOrientation.STATIC;

    darkMenu = false;

    profileMode = 'top';

    rotateMenuButton: boolean;

    topbarMenuActive: boolean;

    overlayMenuActive: boolean;

    // staticMenuDesktopInactive: boolean;

    staticMenuMobileActive: boolean;


    layoutContainer: HTMLDivElement;

    layoutMenuScroller: HTMLDivElement;

    menuClick: boolean;

    topbarItemClick: boolean;

    activeTopbarItem: any;

    resetMenu: boolean;

    menuHoverActive: boolean;


    // rightPanelClick: boolean;
    // rightPanelHoverActive: boolean;
    // rightPanelActive: boolean;

    // overlayRightPanelActive: boolean;
    // staticRightPanelDesktopInactive: boolean;
    // staticRightPanelMobileActive: boolean;
    // resetRightPanel: boolean;

    @Select(TenantService.activeTenants)
    globalTenants$: Observable<Tenant[]>;

    @Select(UserService.userRoles)
    userRoles$: Observable<Role[]>;


    requiredConfigurations$: Observable<any[]>;


    @ViewChild('layoutContainer', { static: false }) layourContainerViewChild: ElementRef;

    @ViewChild('scrollPanel', { static: false }) layoutMenuScrollerViewChild: ScrollPanel;

    @ViewChild('scrollPanelRight', { static: false }) layoutMenuScrollerRightViewChild: ScrollPanel;


    rippleInitListener: any;

    rippleMouseDownListener: any;
    progressMessage: AlertMessage;
    appTitle = 'BeneLynk - Lynk360';
    getMessageEvent$$: Subscription;

    constructor(
        private readonly store: Store,
        public renderer2: Renderer2,
        public zone: NgZone,
        public layoutService: LayoutService,
        public menuService: MenuService,
        private readonly alertService: AlertService,
        private readonly messageService: MessageService,
        private readonly appTitleService: AppTitleService,
        public rightPanelService: RightPanelService,
        public service: MainConfigService,
        lazyLoaderService: LazyLoaderService,
        private readonly mediaService: MediaService,
        public commandLazyDataService: CommandLazyDataService,
    ) {

        super(lazyLoaderService);
        this.appTitleService.appName = this.appTitle;
        this.layoutService.currentLayout$.next(this);
    }

    ngOnInit() {
        this.requiredConfigurations$ = combineLatest(
            [
                this.globalTenants$,
                this.userRoles$,
                this.store.select<Role>(UserService.currentRole)
            ]
        ).pipe(
            filter(([globalTenants, userRoles, currentRole]) => !!globalTenants && !!userRoles),
            switchMap(([globalTenants, userRoles, currentRole]) => {
                const result = [];
                if (!currentRole) {
                    result.push('Selected role');
                }

                return of(result);
            }));


        this.zone.runOutsideAngular(() => { this.bindRipple(); });

        this.getMessageEvent$$ = this.alertService.getMessageEvent().subscribe(message => this.showToast(message));
    }




    bindRipple() {
        this.rippleInitListener = this.init.bind(this);
        document.addEventListener('DOMContentLoaded', this.rippleInitListener);
    }

    init() {
        this.rippleMouseDownListener = this.rippleMouseDown.bind(this);
        document.addEventListener('mousedown', this.rippleMouseDownListener, false);
    }

    rippleMouseDown(e) {
        for (let target = e.target; target && target !== this; target = target['parentNode']) {
            if (!this.isVisible(target)) {
                continue;
            }

            // Element.matches() -> https://developer.mozilla.org/en-US/docs/Web/API/Element/matches
            if (this.selectorMatches(target, '.ripplelink, .ui-button')) {
                const element = target;
                this.rippleEffect(element, e);
                break;
            }
        }
    }

    selectorMatches(el, selector) {
        const p = Element.prototype;
        const f = p['matches'] || p['webkitMatchesSelector'] || p['mozMatchesSelector'] || p['msMatchesSelector'] || function (s) {
            return [].indexOf.call(document.querySelectorAll(s), this) !== -1;
        };
        return f.call(el, selector);
    }

    isVisible(el) {
        return !!(el.offsetWidth || el.offsetHeight);
    }

    rippleEffect(element, e) {
        if (element.querySelector('.ink') === null) {
            const inkEl = document.createElement('span');
            this.addClass(inkEl, 'ink');

            if (this.hasClass(element, 'ripplelink') && element.querySelector('span')) {
                element.querySelector('span').insertAdjacentHTML('afterend', '<span class=\'ink\'></span>');
            } else {
                element.appendChild(inkEl);
            }
        }

        const ink = element.querySelector('.ink');
        this.removeClass(ink, 'ripple-animate');

        if (!ink.offsetHeight && !ink.offsetWidth) {
            const d = Math.max(element.offsetWidth, element.offsetHeight);
            ink.style.height = d + 'px';
            ink.style.width = d + 'px';
        }

        const x = e.pageX - this.getOffset(element).left - (ink.offsetWidth / 2);
        const y = e.pageY - this.getOffset(element).top - (ink.offsetHeight / 2);

        ink.style.top = y + 'px';
        ink.style.left = x + 'px';
        ink.style.pointerEvents = 'none';
        this.addClass(ink, 'ripple-animate');
    }

    hasClass(element, className) {
        if (element.classList) {
            return element.classList.contains(className);
        } else {
            return new RegExp('(^| )' + className + '( |$)', 'gi').test(element.className);
        }
    }

    addClass(element, className) {
        if (element.classList) {
            element.classList.add(className);
        } else {
            element.className += ' ' + className;
        }
    }

    removeClass(element, className) {
        if (element.classList) {
            element.classList.remove(className);
        } else {
            element.className = element.className.replace(new RegExp('(^|\\b)' + className.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
        }
    }

    getOffset(el) {
        const rect = el.getBoundingClientRect();

        return {
            top: rect.top + (window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0),
            left: rect.left + (window.pageXOffset || document.documentElement.scrollLeft || document.body.scrollLeft || 0),
        };
    }

    unbindRipple() {
        if (this.rippleInitListener) {
            document.removeEventListener('DOMContentLoaded', this.rippleInitListener);
        }
        if (this.rippleMouseDownListener) {
            document.removeEventListener('mousedown', this.rippleMouseDownListener);
        }
    }

    ngAfterViewInit() {
        super.ngAfterViewInit();
        // this.layoutContainer = this.layourContainerViewChild.nativeElement as HTMLDivElement;
        // setTimeout(() => { this.layoutMenuScrollerViewChild.moveBar(); }, 100);
    }

    onLayoutClick() {
        if (!this.topbarItemClick) {
            this.activeTopbarItem = null;
            this.topbarMenuActive = false;
        }

        if (!this.menuClick) {
            if (this.service.isHorizontalMenu || this.service.isSlimMenu) {
                this.resetMenu = true;
            }

            if (this.overlayMenuActive || this.staticMenuMobileActive) {
                this.hideOverlayMenu();
            }

            this.menuHoverActive = false;
        }

        if (!this.layoutService.rightPanelClickSync) {
            this.layoutService.setRightPanelActive(false);
        }

        this.topbarItemClick = false;
        this.menuClick = false;
        this.layoutService.setRightPanelClick(false);
    }

    onMenuButtonClick(event) {
        this.menuClick = true;
        this.rotateMenuButton = !this.rotateMenuButton;
        this.topbarMenuActive = false;

        if (this.layoutMode === MenuOrientation.OVERLAY) {
            this.overlayMenuActive = !this.overlayMenuActive;
        } else {
            if (this.isDesktop()) {
                this.service.staticMenuDesktopInactive = !this.service.staticMenuDesktopInactive;
            } else {
                this.staticMenuMobileActive = !this.staticMenuMobileActive;
            }
        }

        event.preventDefault();
    }



    onMenuClick($event) {
        this.menuClick = true;
        this.resetMenu = false;
    }



    onTopbarMenuButtonClick(event) {
        this.topbarItemClick = true;
        this.topbarMenuActive = !this.topbarMenuActive;

        this.hideOverlayMenu();

        event.preventDefault();
    }

    onTopbarItemClick(event, item) {
        this.topbarItemClick = true;

        if (this.activeTopbarItem === item) {
            this.activeTopbarItem = null;
        } else {
            this.activeTopbarItem = item;
        }

        event.preventDefault();
    }

    // onTopbarSubItemClick(event) {
    //     if (event.preventDefault) {
    //         event.preventDefault();
    //     } else if (event.originalEvent.preventDefault) {
    //         event.originalEvent.preventDefault();
    //     }

    //     this.userService.logout();


    // }

    onRightPanelButtonClickFromExternal = this.onRightPanelButtonClick.bind(this);

    onRightPanelButtonClick(event) {
        if (event.preventDefault) {
            event.preventDefault();
        } else if (event.originalEvent.preventDefault) {
            event.originalEvent.preventDefault();
        }

        this.rightPanelService.setCurrentCustomView(LazyLoadedWidgets.COMPONENT_RIGHTPANEL_SETTINGS);

        this.layoutService.setRightPanelClick(true);
        this.layoutService.setRightPanelActive(!this.layoutService.rightPanelActiveSync);
    }

    onRightPanelClick() {
        this.layoutService.setRightPanelClick(true);
    }


    hideOverlayMenu() {
        this.rotateMenuButton = false;
        this.overlayMenuActive = false;
        this.staticMenuMobileActive = false;
    }

    hideOverlayRightPanel() {
        // this.rotateMenuButton = false;
        this.layoutService.setOverlayRightPanelActive(false);
        this.layoutService.setStaticRightPanelMobileActive(false);
    }

    isTablet() {
        const width = window.innerWidth;
        return width <= 1024 && width > 640;
    }

    isDesktop() {
        return window.innerWidth > 1024;
    }

    isMobile() {
        return window.innerWidth <= 640;
    }

    // isOverlay() {
    //     return this.layoutMode === MenuOrientation.OVERLAY;
    // }

    // isHorizontal() {
    //     return this.layoutMode === MenuOrientation.HORIZONTAL;
    // }

    // isSlim() {
    //     return this.layoutMode === MenuOrientation.SLIM;
    // }

    // changeToStaticMenu() {
    //     this.layoutMode = MenuOrientation.STATIC;
    // }

    // changeToOverlayMenu() {
    //     this.layoutMode = MenuOrientation.OVERLAY;
    // }

    // changeToHorizontalMenu() {
    //     this.layoutMode = MenuOrientation.HORIZONTAL;
    // }

    // changeToSlimMenu() {
    //     this.layoutMode = MenuOrientation.SLIM;
    // }

    ngOnDestroy() {
        this.unbindRipple();
    }

    showToast(alert: AlertCommand) {

        if (alert.operation === 'clear') {
            this.messageService.clear();
            this.progressMessage = null;
            return;
        }

        let isSticky = false;

        if (alert.operation === 'add_sticky') {
            isSticky = true;
        }

        if (alert.message.severity === MessageSeverity.loading) {
            this.progressMessage = alert.message;
        } else {
            this.messageService.add({
                severity: alert.message.severity,
                summary: alert.message.summary,
                detail: alert.message.detail,
                sticky: isSticky
            } as Message);
        }
    }

    // get userName(): string {
    //     return this.userService.userSync ? this.userService.userSync.userName : '';
    // }

}
